import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Locale = 'en' | 'zh-Hans' | 'zh-Hant'

/**
 * Best-effort first-run guess from the browser's own language, used only
 * until the person picks explicitly — after that, the persisted choice
 * always wins. `zh-TW`/`zh-HK`/`zh-MO`/`zh-Hant*` map to Traditional;
 * any other `zh*` (zh-CN, zh-SG, bare `zh`, …) maps to Simplified.
 */
function detectDefaultLocale(): Locale {
  const lang = (navigator.language || '').toLowerCase()
  if (!lang.startsWith('zh')) return 'en'
  if (lang.includes('hant') || lang.includes('-tw') || lang.includes('-hk') || lang.includes('-mo')) {
    return 'zh-Hant'
  }
  return 'zh-Hans'
}

interface LanguageState {
  locale: Locale
  setLocale: (locale: Locale) => void
}

/**
 * Persisted like progressStore (localStorage via zustand/persist) so a
 * chosen language survives reloads. Separate store rather than folding
 * into progressStore — this is a UI preference, not explorer progress,
 * and keeping it isolated means it can be read from anywhere (i18n
 * helpers, non-React scripts) without pulling in the much larger
 * progress store.
 */
export const useLanguageStore = create<LanguageState>()(
  persist(
    (set) => ({
      locale: detectDefaultLocale(),
      setLocale: (locale) => set({ locale }),
    }),
    {
      name: 'kaleido-language',
    },
  ),
)
